import React from "react";
import closeIcon from "@src/assets/svg/close-icon.svg";
import OTAReleaseNotes from "../../../molecules/ota-release-notes/ota-release-notes";
import styles from "./ota-modal.module.scss";
import {
  ActiveInstallationProcessPayload,
  ListItem,
} from "@src/types/ota-update";
import { OTA_MODAL as cssClassName } from "../../../../cloud-connect/ota-update/constants";
import { GlassyModalHeader } from "@src/components/molecules/glassy-modal-header/glassy-modal-header";
import { GlassyModal } from "../glassy-modal/glassy-modal";
import { Button } from "@src/components/atoms/buttons/button/button";
import { FlexRowContainer } from "@src/components/templates/layouts/flex-row-container/flex-row-container";
import { Spacer16 } from "@src/components/atoms/spacer-16/spacer-16";
import { RenderIfUserHasPermission } from "@src/components/molecules/render-if-user-has-permission/render-if-user-has-permission";
import { RenderIfUserHasMultiplePermissions } from "@src/components/molecules/render-if-user-has-multiple-permissions/render-if-user-has-multiple-permissions";
import { useOtaModal } from "./use-ota-modal";

interface OTAModalProps {
  isOpen: boolean;
  closeFirmwareModal: () => void;
  selectedRow: ListItem | null;
  onInstallStarted?: (updateId: string) => void;
  fetchMainList: () => Promise<void>;
  fetchBackups: () => Promise<void>;
  onSelectPushFirmwareRow?: React.Dispatch<
    React.SetStateAction<ListItem | null>
  >;
  onSelectPushFirmwareBackupRow?: React.Dispatch<
    React.SetStateAction<ListItem | null>
  >;
  activeOTAHistoryTab?: string;
  activeInstallationData?: ActiveInstallationProcessPayload | null;
}

const OTAModal = ({
  isOpen,
  closeFirmwareModal,
  selectedRow,
  onInstallStarted,
  fetchMainList,
  fetchBackups,
  onSelectPushFirmwareRow,
  onSelectPushFirmwareBackupRow,
  activeOTAHistoryTab,
  activeInstallationData,
}: OTAModalProps) => {
  const {
    texts,
    firmwareFields,
    deletePermission,
    downloadPermission,
    isRollbackDisabled,
    installActionTooltip,
    handleDownload,
    handleNewInstall,
    handleDelete,
    shouldShowInstallButton,
  } = useOtaModal({
    selectedRow,
    closeFirmwareModal,
    onInstallStarted,
    fetchMainList,
    fetchBackups,
    onSelectPushFirmwareRow,
    onSelectPushFirmwareBackupRow,
    activeOTAHistoryTab,
    activeInstallationData,
  });

  if (!isOpen || !selectedRow) return null;

  return (
    <div className={styles[cssClassName]}>
      <GlassyModal>
        <div className={styles[`${cssClassName}__container`]}>
          <GlassyModalHeader
            title={texts.detailsText}
            rightIcon={closeIcon}
            rightIconFunction={closeFirmwareModal}
          />

          <div className={styles[`${cssClassName}__modal-content`]}>
            <div className={styles[`${cssClassName}__firmware`]}>
              {firmwareFields.map(({ label, value }) => (
                <div
                  key={label}
                  className={styles[`${cssClassName}__firmware-item`]}
                >
                  <span className={styles[`${cssClassName}__firmware-label`]}>
                    {label}
                  </span>
                  <span className={styles[`${cssClassName}__firmware-value`]}>
                    {value}
                  </span>
                </div>
              ))}
            </div>

            <Spacer16>
              <OTAReleaseNotes cardData={selectedRow} />
            </Spacer16>

            <FlexRowContainer>
              {shouldShowInstallButton && (
                <RenderIfUserHasPermission permission="initiate-update-process">
                  <Button
                    fun={handleNewInstall}
                    text={texts.pushFirmwareText}
                    variant="primary"
                    disabled={isRollbackDisabled}
                    tooltip={installActionTooltip}
                  />
                </RenderIfUserHasPermission>
              )}

              <RenderIfUserHasMultiplePermissions
                permissions={[
                  "initiate-update-process",
                  downloadPermission || "",
                ]}
              >
                <Button
                  fun={() =>
                    handleDownload(selectedRow?.firmware_id, selectedRow?.type)
                  }
                  text={texts.downloadButtonText}
                  variant="secondary"
                />
              </RenderIfUserHasMultiplePermissions>

              {deletePermission && (
                <RenderIfUserHasPermission permission={deletePermission}>
                  <Button
                    fun={() =>
                      handleDelete(selectedRow?.firmware_id, selectedRow?.type)
                    }
                    text={texts.deleteButtonText}
                    variant={["secondary", "alert"]}
                  />
                </RenderIfUserHasPermission>
              )}
            </FlexRowContainer>
          </div>
        </div>
      </GlassyModal>
    </div>
  );
};

export default OTAModal;
